/**
 * NamedDefaultAddFormBody — orchestrator for the `add-named-default` form.
 * mssql-only. Surfaces a "Form" radio (Inline vs Standalone) that toggles
 * between the modern `ALTER TABLE … ADD CONSTRAINT df_name DEFAULT (expr)
 * FOR col` form and the deprecated `CREATE DEFAULT df_name AS expr`
 * standalone form. The standalone form hides the column field.
 *
 * Engine gating: caller (`NamedDefaultAddPopover`) renders the trigger
 * disabled on non-mssql engines with a tooltip pointing at the column form
 * (Slice A1's `change.default`). Included as a defensive runtime guard.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B5.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import type { NamedDefaultDef } from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAddNamedDefault,
  validateNamedDefaultForEngine,
} from '../../edit/edits/addNamedDefault';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

type FormMode = 'inline' | 'standalone';

export interface NamedDefaultAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function NamedDefaultAddFormBody(props: NamedDefaultAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [mode, setMode] = useState<FormMode>('inline');
  const [name, setName] = useState('');
  const [expression, setExpression] = useState('');
  const [column, setColumn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildDef(): NamedDefaultDef | { error: string } {
    const draft: { name: string; expression: string; column?: string; standalone?: boolean } = {
      name: name.trim(),
      expression,
    };
    if (mode === 'standalone') {
      draft.standalone = true;
    } else {
      const trimmedCol = column.trim();
      if (trimmedCol !== '') draft.column = trimmedCol;
    }
    const err = validateNamedDefaultForEngine(engine, draft);
    if (err !== null) return { error: err };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    const built = buildDef();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddNamedDefault(editContext, { namedDefault: built });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add named DEFAULT</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1" data-testid={`${testId}-form-mode`}>
        <Label className="text-xs">Form</Label>
        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as FormMode)}
          className="flex gap-3 text-xs"
        >
          <label className="flex items-center gap-1">
            <RadioGroupItem value="inline" data-testid={`${testId}-mode-inline`} />
            <span>Inline (recommended)</span>
          </label>
          <label className="flex items-center gap-1">
            <RadioGroupItem value="standalone" data-testid={`${testId}-mode-standalone`} />
            <span>Standalone (deprecated)</span>
          </label>
        </RadioGroup>
        {mode === 'standalone' ? (
          <span className="text-[10px] text-muted-foreground" data-testid={`${testId}-standalone-note`}>
            CREATE DEFAULT was deprecated in SQL Server 2008. Bind via sp_bindefault separately.
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="df_table_status"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-expression`} className="text-xs">
          Expression <span className="text-destructive">*</span>
        </Label>
        <textarea
          id={`${testId}-expression`}
          data-testid={`${testId}-expression`}
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          placeholder="'active'"
          className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
        />
        <span className="text-[10px] text-muted-foreground">
          {mode === 'inline'
            ? 'Verbatim expression — synthesizer wraps it in parentheses for the inline form.'
            : 'Verbatim expression — the standalone CREATE DEFAULT form does not wrap.'}
        </span>
      </div>

      {mode === 'inline' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-column`} className="text-xs">
            Column <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-column`}
            data-testid={`${testId}-column`}
            value={column}
            onChange={(e) => setColumn(e.target.value)}
            placeholder="Status"
            className="h-8 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">
            Bind target — emits FOR &lt;column&gt; on the ALTER TABLE form.
          </span>
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
